/**
 * Pure helpers for scm:listview — column prefs, response mapping, cell display, range text.
 * Kept separate from the React component for unit testing and Lookup-safe reuse.
 */

import { IsJsonString, isSet } from "../../../Utils/CommonFunctions";
import { getLoggedInUserId } from "../../../Utils/UserFunctions";

export const ANONYMOUS_USER_ID = "anonymous";

/** Resolve logged-in user id for preference keys; falls back to anonymous when missing. */
export const resolveListViewUserId = () => {
  try {
    const id = getLoggedInUserId();
    if (id != null && String(id).trim() !== "") {
      return String(id);
    }
  } catch (e) {
    // ignore
  }
  return ANONYMOUS_USER_ID;
};

/**
 * Preference key:
 * pages_listview_columns:{userId}:{action}:{listviewId}
 */
export const buildListViewColumnsPrefKey = (userId, action, listviewId) => {
  const uid = userId != null && String(userId).trim() !== "" ? String(userId) : ANONYMOUS_USER_ID;
  const act = action != null ? String(action) : "";
  const lid = listviewId != null ? String(listviewId) : "";
  return `pages_listview_columns:${uid}:${act}:${lid}`;
};

/**
 * Resolve list instance id for prefs / DOM.
 * MarkupConversion injects a numeric itemIndex when markup omits id
 * (`id: isSet(attribs.id, itemIndex)`). Prefer author `name` in that case
 * so documented name-fallback works.
 */
export const resolveListViewInstanceId = (attributes = {}) => {
  const rawId = attributes.id != null ? attributes.id : attributes.Id;
  const name = String(attributes.name || attributes.Name || "").trim();
  const idIsGeneratedIndex = typeof rawId === "number";
  if (!idIsGeneratedIndex && rawId != null && String(rawId).trim() !== "") {
    return String(rawId).trim();
  }
  if (name) return name;
  if (rawId != null && String(rawId).trim() !== "") return String(rawId).trim();
  return "listview";
};

/** Build default columns from scm:field list (markup order, all visible). */
export const buildDefaultColumns = (resultFields = []) => {
  return (resultFields || [])
    .filter((f) => f && (f.key || f.label))
    .map((f, index) => {
      const type = String(f.type || f.fieldType || "");
      return {
        key: String(f.key || ""),
        label: String(f.label || f.key || ""),
        type,
        fieldType: type,
        isShow: true,
        position: index + 1,
      };
    });
};

/**
 * Merge saved prefs onto markup fields.
 * - Ignore saved keys not in markup
 * - New markup fields visible by default, appended after saved order
 */
export const mergeColumnPreferences = (resultFields = [], savedPrefs = null) => {
  const defaults = buildDefaultColumns(resultFields);
  if (!Array.isArray(savedPrefs) || savedPrefs.length === 0) {
    return defaults;
  }

  const byKey = new Map(defaults.map((c) => [c.key, { ...c }]));
  const ordered = [];
  const seen = new Set();

  const sortedSaved = [...savedPrefs].sort((a, b) => {
    const pa = Number(a?.position);
    const pb = Number(b?.position);
    const na = Number.isFinite(pa) ? pa : Number.MAX_SAFE_INTEGER;
    const nb = Number.isFinite(pb) ? pb : Number.MAX_SAFE_INTEGER;
    return na - nb;
  });

  sortedSaved.forEach((pref) => {
    const key = pref?.key != null ? String(pref.key) : "";
    if (!key || !byKey.has(key) || seen.has(key)) return;
    const base = byKey.get(key);
    ordered.push({
      ...base,
      isShow: pref.is_show === true || pref.is_show === "true",
      position: ordered.length + 1,
    });
    seen.add(key);
  });

  defaults.forEach((col) => {
    if (!seen.has(col.key)) {
      ordered.push({ ...col, isShow: true, position: ordered.length + 1 });
      seen.add(col.key);
    }
  });

  // Never leave all columns hidden — if merge yields zero visible, show all
  if (ordered.length > 0 && !ordered.some((c) => c.isShow)) {
    return ordered.map((c, i) => ({ ...c, isShow: true, position: i + 1 }));
  }

  return ordered.map((c, i) => ({ ...c, position: i + 1 }));
};

export const columnsToPrefPayload = (columns = []) => {
  return (columns || []).map((c, index) => ({
    key: c.key,
    is_show: c.isShow === true,
    position: index + 1,
  }));
};

export const loadListViewColumnPrefs = (storageKey, storage = localStorage) => {
  try {
    if (!storage || !storageKey) return null;
    const raw = storage.getItem(storageKey);
    if (raw == null || raw === "") return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
};

export const saveListViewColumnPrefs = (storageKey, columns, storage = localStorage) => {
  try {
    if (!storage || !storageKey) return false;
    storage.setItem(storageKey, JSON.stringify(columnsToPrefPayload(columns)));
    return true;
  } catch (e) {
    return false;
  }
};

/** CardBox data shape from columns */
export const columnsToCardBoxData = (columns = []) => {
  return (columns || []).map((c) => ({
    id: c.key,
    display_name: c.label || c.key,
    isShow: c.isShow === true,
  }));
};

/**
 * Toggle column visibility by key. Prevents hiding the last visible column.
 */
export const toggleColumnVisibility = (columns, cardId) => {
  const list = (columns || []).map((c) => ({ ...c }));
  const target = list.find((c) => c.key === cardId);
  if (!target) return list;

  const visibleCount = list.filter((c) => c.isShow).length;
  if (target.isShow && visibleCount <= 1) {
    return list;
  }

  target.isShow = !target.isShow;
  const checked = list.filter((c) => c.isShow);
  const unchecked = list.filter((c) => !c.isShow);
  return [...checked, ...unchecked].map((c, i) => ({ ...c, position: i + 1 }));
};

/** Reorder columns after CardBox drag-drop (smooth-dnd removedIndex/addedIndex). */
export const reorderColumns = (columns, removedIndex, addedIndex) => {
  if (
    removedIndex == null ||
    addedIndex == null ||
    removedIndex === addedIndex ||
    removedIndex < 0 ||
    addedIndex < 0
  ) {
    return columns || [];
  }
  const list = [...(columns || [])];
  if (removedIndex >= list.length) return list;
  const [item] = list.splice(removedIndex, 1);
  list.splice(addedIndex, 0, item);
  return list.map((c, i) => ({ ...c, position: i + 1 }));
};

/**
 * Map pages-config-execute response variables via to_page_mapping → pages_variable_name.
 * Same approach as LookupModal.
 */
export const mapExecuteResponseByPagesVariableName = (responseVariables, toPageMapping) => {
  const variableMapping = {};
  (toPageMapping || []).forEach((toMap) => {
    if (toMap.field_mappings && Array.isArray(toMap.field_mappings)) {
      toMap.field_mappings.forEach((mField) => {
        if (mField.automation_variable_id && mField.pages_variable_name) {
          variableMapping[mField.automation_variable_id] = mField.pages_variable_name;
        }
      });
    }
  });

  const responseVariableMap = {};
  (responseVariables || []).forEach((responseVar) => {
    const pageVariableName = variableMapping[responseVar.id];
    if (pageVariableName) {
      responseVariableMap[pageVariableName] = responseVar;
    }
  });
  return responseVariableMap;
};

export const parseListRowsFromVariable = (resultVariable) => {
  if (!resultVariable) return [];
  try {
    const recordsData = isSet(resultVariable.value, resultVariable.selected_value);
    let resRecordsData = IsJsonString(recordsData) ? JSON.parse(recordsData) : recordsData;
    if (!Array.isArray(resRecordsData)) {
      resRecordsData = resRecordsData ? [resRecordsData] : [];
    }
    return resRecordsData;
  } catch (e) {
    return [];
  }
};

export const parseTotalRecordsFromVariable = (totalRecordVariable, fallbackLength = 0) => {
  if (!totalRecordVariable) return fallbackLength;
  const totalVal = isSet(totalRecordVariable.value, totalRecordVariable.selected_value);
  const num = parseInt(totalVal, 10);
  if (!Number.isNaN(num) && num >= 0) return num;
  return fallbackLength;
};

/**
 * Extract rows + total from execute response using configured names.
 * Returns { rows, totalRecords, mapped: true|false, error?: string }
 */
export const extractListViewExecuteResult = ({
  responseVariables,
  toPageMapping,
  resultVar,
  totalRecordVar,
}) => {
  if (!resultVar) {
    return {
      rows: [],
      totalRecords: 0,
      mapped: false,
      error: "Result variable is not configured on scm:result.",
    };
  }

  const responseVariableMap = mapExecuteResponseByPagesVariableName(
    responseVariables,
    toPageMapping
  );

  if (!responseVariableMap[resultVar]) {
    return {
      rows: [],
      totalRecords: 0,
      mapped: false,
      error: `Mapped result variable "${resultVar}" was not found in the Page Configuration response.`,
    };
  }

  const rows = parseListRowsFromVariable(responseVariableMap[resultVar]);
  let totalRecords = rows.length;
  if (totalRecordVar) {
    if (!responseVariableMap[totalRecordVar]) {
      return {
        rows: [],
        totalRecords: 0,
        mapped: false,
        error: `Mapped total variable "${totalRecordVar}" was not found in the Page Configuration response.`,
      };
    }
    totalRecords = parseTotalRecordsFromVariable(
      responseVariableMap[totalRecordVar],
      rows.length
    );
  }

  return { rows, totalRecords, mapped: true, error: null };
};

/** Read nested value by dot path (Lookup-compatible). */
export const getValueByPath = (row, path) => {
  if (row == null || path == null || path === "") return undefined;
  const keys = String(path).split(".").filter(Boolean);
  if (keys.length === 0) return undefined;
  return keys.reduce((obj, key) => {
    if (obj == null || typeof obj !== "object") return undefined;
    return obj[key];
  }, row);
};

/** Normalize scm:field type / fieldType for list-cell display. */
export const normalizeListViewFieldType = (fieldType) => {
  if (fieldType == null) return "";
  return String(fieldType).trim().toLowerCase().replace(/[\s_-]+/g, "");
};

const LISTVIEW_CHECKBOX_TYPES = new Set(["checkbox", "checkboxes", "boolean"]);

export const isListViewCheckboxFieldType = (fieldType) =>
  LISTVIEW_CHECKBOX_TYPES.has(normalizeListViewFieldType(fieldType));

const LISTVIEW_DATE_TYPES = new Set(["date"]);
const LISTVIEW_DATETIME_TYPES = new Set(["datetime", "timestamp"]);
const LISTVIEW_CURRENCY_TYPES = new Set(["currency"]);

export const isListViewCurrencyFieldType = (fieldType) =>
  LISTVIEW_CURRENCY_TYPES.has(normalizeListViewFieldType(fieldType));
const LISTVIEW_PERCENT_TYPES = new Set(["percentage", "percent"]);
const LISTVIEW_IMAGE_TYPES = new Set(["imageupload", "image", "file"]);
const LISTVIEW_BARCODE_TYPES = new Set(["barcode"]);
const LISTVIEW_URL_TYPES = new Set(["url", "hyperlink", "hyperlinkfield"]);

const parseJsonLikeCellValue = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) return value;
  if (!IsJsonString(trimmed)) return value;
  try {
    return JSON.parse(trimmed);
  } catch (e) {
    return value;
  }
};

const pad2 = (n) => String(n).padStart(2, "0");

const formatListViewDateValue = (value, withTime) => {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    const inner = value.value ?? value.date ?? value.datetime ?? value.label;
    if (inner != null && inner !== value) {
      return formatListViewDateValue(inner, withTime);
    }
    return formatListViewGenericValue(value);
  }
  const str = String(value).trim();
  if (!str) return "";
  const dateOnly = str.match(/^(\d{4}-\d{2}-\d{2})/);
  if (!withTime && dateOnly) return dateOnly[1];
  const localDt = str.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}):(\d{2})/);
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(str);
  if (withTime && localDt && !hasZone) {
    return `${localDt[1]} ${localDt[2]}:${localDt[3]}`;
  }
  const parsed = new Date(str);
  if (Number.isNaN(parsed.getTime())) return str;
  const datePart = `${parsed.getFullYear()}-${pad2(parsed.getMonth() + 1)}-${pad2(
    parsed.getDate()
  )}`;
  if (!withTime) return datePart;
  return `${datePart} ${pad2(parsed.getHours())}:${pad2(parsed.getMinutes())}`;
};

const formatListViewCheckboxValue = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    return formatListViewCheckboxValue(
      value.value ?? value.selected_value ?? value.label ?? value.name
    );
  }
  if (value === true || value === 1 || value === "1") return "Yes";
  if (value === false || value === 0 || value === "0") return "No";
  const s = String(value).trim().toLowerCase();
  if (["true", "yes", "y", "on"].includes(s)) return "Yes";
  if (["false", "no", "n", "off"].includes(s)) return "No";
  return String(value);
};

/** Split a currency cell into amount + ISO code so the list can render them apart. */
export const parseListViewCurrencyParts = (value) => {
  if (value == null || value === "") return { amount: "", code: "" };
  const parsed = parseJsonLikeCellValue(value);
  if (typeof parsed === "number" || typeof parsed === "boolean") {
    return { amount: String(parsed), code: "" };
  }
  if (typeof parsed === "string") {
    const trimmed = parsed.trim();
    if (!trimmed) return { amount: "", code: "" };
    const withCode = trimmed.match(/^(.*)\s+([A-Za-z]{3})$/);
    if (withCode && withCode[1].trim() !== "") {
      return { amount: withCode[1].trim(), code: withCode[2].toUpperCase() };
    }
    return { amount: trimmed, code: "" };
  }
  if (typeof parsed === "object") {
    const amount = parsed.amount ?? parsed.value ?? parsed.converted_amount;
    const code = parsed.currency_code ?? parsed.currency ?? parsed.code;
    let amountText = "";
    if (amount != null && typeof amount === "object") {
      amountText = parseListViewCurrencyParts(amount).amount;
    } else if (amount != null && amount !== "") {
      amountText = String(amount);
    }
    const codeText =
      code != null && code !== "" && typeof code !== "object" ? String(code) : "";
    if (!amountText && !codeText) {
      return { amount: formatListViewGenericValue(parsed), code: "" };
    }
    return { amount: amountText, code: codeText };
  }
  return { amount: String(parsed), code: "" };
};

const formatListViewCurrencyValue = (value) => {
  const { amount, code } = parseListViewCurrencyParts(value);
  if (amount && code) return `${amount} ${code}`;
  return amount || code || "";
};

const extractFileNameFromPath = (url) => {
  const s = String(url || "").trim();
  if (!s) return "";
  const noQuery = s.split("?")[0];
  const parts = noQuery.split("/");
  return parts[parts.length - 1] || s;
};

export const extractListViewMediaUrl = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "string") {
    const s = value.trim();
    if (!s) return "";
    if (/^(https?:|data:|\/)/i.test(s) || /\.(png|jpe?g|gif|webp|svg|pdf)(\?|$)/i.test(s)) {
      return s;
    }
    return "";
  }
  if (typeof value === "object") {
    return extractListViewMediaUrl(
      value.url ||
        value.path ||
        value.file_url ||
        value.src ||
        value.value ||
        value.name
    );
  }
  return "";
};

const formatListViewFileValue = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "string") {
    if (/^(https?:|data:|\/)/i.test(value) || value.includes("/")) {
      return extractFileNameFromPath(value);
    }
    return value;
  }
  if (typeof value === "object") {
    const name = value.name || value.file_name || value.filename || value.label;
    if (name) return String(name);
    const url = extractListViewMediaUrl(value);
    return url ? extractFileNameFromPath(url) : formatListViewGenericValue(value);
  }
  return String(value);
};

/** Safe cell display — never "[object Object]". */
export const formatListViewGenericValue = (value) => {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    try {
      return value
        .map((item) => {
          if (item == null) return "";
          if (typeof item === "object") {
            return formatListViewGenericValue(item);
          }
          return String(item);
        })
        .filter((s) => s !== "")
        .join(", ");
    } catch (e) {
      return "";
    }
  }
  if (typeof value === "object") {
    const namedKeys = [
      "label",
      "name",
      "display_name",
      "full_name",
      "carrier_name",
      "account_name",
      "company_name",
      "title",
    ];
    for (let i = 0; i < namedKeys.length; i += 1) {
      const named = value[namedKeys[i]];
      if (named != null && named !== "" && typeof named !== "object") {
        return String(named);
      }
    }
    if (value.first_name || value.last_name) {
      return [value.first_name, value.last_name].filter(Boolean).join(" ");
    }
    const skipName = /password|secret|token|login/i;
    const extraNameKey = Object.keys(value).find((k) => {
      if (!/(_name|name)$/i.test(k) || skipName.test(k)) return false;
      const named = value[k];
      return named != null && named !== "" && typeof named !== "object";
    });
    if (extraNameKey) return String(value[extraNameKey]);
    if ("value" in value && value.value != null && typeof value.value !== "object") {
      return String(value.value);
    }
    if ("selected_value" in value) {
      return formatListViewGenericValue(value.selected_value);
    }
    return "";
  }
  return "";
};

/** Safe cell display — never "[object Object]". Type comes from markup, not window names. */
export const formatListViewCellValue = (value, fieldType) => {
  if (value == null) return "";
  const parsed = parseJsonLikeCellValue(value);
  const kind = normalizeListViewFieldType(fieldType);

  if (LISTVIEW_CHECKBOX_TYPES.has(kind)) return formatListViewCheckboxValue(parsed);
  if (LISTVIEW_DATE_TYPES.has(kind)) return formatListViewDateValue(parsed, false);
  if (LISTVIEW_DATETIME_TYPES.has(kind)) return formatListViewDateValue(parsed, true);
  if (LISTVIEW_CURRENCY_TYPES.has(kind)) return formatListViewCurrencyValue(parsed);
  if (LISTVIEW_PERCENT_TYPES.has(kind)) {
    if (typeof parsed === "number") return `${parsed}%`;
    if (typeof parsed === "string") {
      return parsed.includes("%") ? parsed : parsed === "" ? "" : `${parsed}%`;
    }
    if (typeof parsed === "object") {
      return formatListViewCellValue(
        parsed.value ?? parsed.label ?? parsed.selected_value,
        fieldType
      );
    }
  }
  if (LISTVIEW_IMAGE_TYPES.has(kind)) return formatListViewFileValue(parsed);
  if (LISTVIEW_BARCODE_TYPES.has(kind) || LISTVIEW_URL_TYPES.has(kind)) {
    if (typeof parsed === "object") {
      return formatListViewGenericValue(parsed) || extractListViewMediaUrl(parsed);
    }
    return parsed == null ? "" : String(parsed);
  }
  return formatListViewGenericValue(parsed);
};

/**
 * Showing X-Y of Z — page is 0-based page index.
 * For zero total, returns zeros / empty range indicators.
 */
export const calculateShowingRange = (page, perPage, totalRecords, returnedRowCount) => {
  const safePage = Math.max(0, Number(page) || 0);
  const safePerPage = Math.max(1, Number(perPage) || 10);
  const safeTotal = Math.max(0, Number(totalRecords) || 0);
  const rowCount = Math.max(0, Number(returnedRowCount) || 0);

  if (safeTotal === 0 || rowCount === 0) {
    return { start: 0, end: 0, total: safeTotal };
  }

  const start = safePage * safePerPage + 1;
  const end = Math.min(start + rowCount - 1, safeTotal);
  return { start, end, total: safeTotal };
};

export const formatShowingRangeText = (page, perPage, totalRecords, returnedRowCount) => {
  const { start, end, total } = calculateShowingRange(
    page,
    perPage,
    totalRecords,
    returnedRowCount
  );
  if (total === 0) {
    return "Showing 0 of 0 records";
  }
  return `Showing ${start}-${end} of ${total} records`;
};

const listViewTagName = (node) => String(node?.name || "").toLowerCase();

const isListViewTagNamed = (node, localName) => {
  if (!node || node.type !== "tag") return false;
  const name = listViewTagName(node);
  const local = String(localName || "").toLowerCase();
  return name === local || name.endsWith(`:${local}`);
};

const collectListViewButtonNodes = (children = []) => {
  return (children || []).filter((node) => isListViewTagNamed(node, "button"));
};

const findListViewChildTag = (children, localName) => {
  return (children || []).find((node) => isListViewTagNamed(node, localName)) || null;
};

const readListViewBooleanAttr = (attributes = {}, names = []) => {
  for (let i = 0; i < names.length; i += 1) {
    const raw = attributes[names[i]];
    if (raw === undefined || raw === null || raw === "") continue;
    if (raw === true || raw === 1) return true;
    if (raw === false || raw === 0) return false;
    const s = String(raw).trim().toLowerCase();
    if (s === "true" || s === "1" || s === "yes") return true;
    if (s === "false" || s === "0" || s === "no") return false;
  }
  return null;
};

/**
 * Nested scm:button tags on scm:listview (Carrier-style left toolbar).
 * Direct children only — ignores scm:config, scm:row-actions, scm:mass-actions,
 * and text/whitespace nodes.
 */
export const getListViewToolbarNodes = (children = []) => {
  return collectListViewButtonNodes(children);
};

/**
 * Buttons inside scm:row-actions, grouped by slot.
 * slot="edit" | slot="delete" | anything else (including omitted) → more.
 */
export const getListViewRowActionNodes = (children = []) => {
  const result = { edit: [], delete: [], more: [] };
  const container =
    findListViewChildTag(children, "row-actions") ||
    findListViewChildTag(children, "rowactions");
  if (!container) return result;
  collectListViewButtonNodes(container.children).forEach((node) => {
    const slot = String(
      node.attribs?.slot || node.attribs?.Slot || ""
    )
      .trim()
      .toLowerCase();
    if (slot === "edit") result.edit.push(node);
    else if (slot === "delete") result.delete.push(node);
    else result.more.push(node);
  });
  return result;
};

/** Buttons inside scm:mass-actions (header overlay three-dots). */
export const getListViewMassActionNodes = (children = []) => {
  const container =
    findListViewChildTag(children, "mass-actions") ||
    findListViewChildTag(children, "massactions");
  if (!container) return [];
  return collectListViewButtonNodes(container.children);
};

/**
 * Leading action column when row-actions / mass-actions exist, or rowSelect="true".
 */
export const isListViewActionColumnEnabled = (
  attributes = {},
  rowActions = { edit: [], delete: [], more: [] },
  massActions = []
) => {
  const explicit = readListViewBooleanAttr(attributes, [
    "rowSelect",
    "rowselect",
    "row-select",
  ]);
  if (explicit === true) return true;
  const ra = rowActions || { edit: [], delete: [], more: [] };
  const ma = massActions || [];
  return (
    (ra.edit && ra.edit.length > 0) ||
    (ra.delete && ra.delete.length > 0) ||
    (ra.more && ra.more.length > 0) ||
    ma.length > 0
  );
};

export const getRowIdentity = (row, recordIdField, fallbackIndex) => {
  if (row != null && recordIdField) {
    const raw = getValueByPath(row, recordIdField);
    if (raw != null && raw !== "") return String(raw);
  }
  if (row != null && row.id != null && row.id !== "") return `fallback-id-${row.id}`;
  return `row-${fallbackIndex}`;
};

export const getListViewPageIds = (rows, recordIdField) => {
  return (rows || []).map((row, index) => getRowIdentity(row, recordIdField, index));
};

export const toggleListViewSelectedId = (selectedIds, id) => {
  const list = Array.isArray(selectedIds) ? [...selectedIds] : [];
  const key = String(id);
  const idx = list.findIndex((item) => String(item) === key);
  if (idx >= 0) {
    list.splice(idx, 1);
    return list;
  }
  list.push(key);
  return list;
};

export const isListViewPageFullySelected = (pageIds, selectedIds, isSelectAll) => {
  if (isSelectAll) return true;
  const ids = pageIds || [];
  if (ids.length === 0) return false;
  const selected = new Set((selectedIds || []).map(String));
  return ids.every((id) => selected.has(String(id)));
};

export const isListViewRowSelected = (rowKey, selectedIds, isSelectAll) => {
  if (isSelectAll) return true;
  const key = String(rowKey);
  return (selectedIds || []).some((id) => String(id) === key);
};

export const getListViewSelectedCount = (selectedIds, isSelectAll, totalRecords) => {
  if (isSelectAll) return Math.max(0, Number(totalRecords) || 0);
  return Array.isArray(selectedIds) ? selectedIds.length : 0;
};

export const buildListViewMassLoopData = ({
  selectedIds = [],
  isSelectAll = false,
  rows = [],
  recordIdField,
  search = "",
  totalRecords = 0,
} = {}) => {
  const selectedSet = new Set((selectedIds || []).map(String));
  const records = (rows || []).filter((row, index) =>
    selectedSet.has(getRowIdentity(row, recordIdField, index))
  );
  return {
    record_ids: Array.isArray(selectedIds) ? selectedIds : [],
    records,
    is_select_all: isSelectAll === true,
    search: search || "",
    totalRecords: Math.max(0, Number(totalRecords) || 0),
  };
};
